<script>
import FormSubmissionContainer from '@/components/forms/form-submission-container.vue'
import BaseButton from '@/components/general/_base-button.vue'
import { mapActions } from 'vuex'
import config from '@/common/config'
import baseMixin from '@/mixins/baseMixin'
import Spinner from '@/components/general/spinner.vue'
import RegistrationStepCodeName from '@/constants/registration/RegistrationStepCodeName'
import RegistrationStepStatus from '@/constants/registration/RegistrationStepStatus'

export default {
  name: 'CandidateInterviewForm',
  components: {
    FormSubmissionContainer,
    BaseButton,
    Spinner,
  },
  mixins: [baseMixin],
  props: {
    candidateId: {
      type: String,
      required: true,
    },
    registrationId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedTemplateId: null,
      templateOptions: this.parseJson(
        config.get('registrations.interviewForms')
      ),
      isReadOnly: true,
      newSubmission: null,
      existingSubmission: null,
      completeWithNoSubmission: false,
      errorMessage: null,
      hasMessageListener: false,
      targetOrigin: config.get('reliefRosterUrl'),
      loading: true,
    }
  },
  computed: {
    title() {
      return this.newSubmission && this.selectedTemplate?.title
        ? this.$t('forms.interview.submissionTitle', [
            this.selectedTemplate.title,
          ])
        : this.$t('forms.interview.title')
    },
    selectedTemplate() {
      return (
        this.templateOptions?.find(
          ({ templateId }) => templateId === this.selectedTemplateId
        ) || { title: '', templateId: null }
      )
    },
    showSubmissionContainer() {
      return (
        this.isReadOnly && (this.formResponsePasskey ?? this.selectedTemplateId)
      )
    },
    showCreateSubmissionUI() {
      return (
        !this.completeWithNoSubmission &&
        !this.loading &&
        this.isReadOnly &&
        !this.existingSubmission?.passkey
      )
    },
    formResponsePasskey() {
      return this.newSubmission?.passkey ?? this.existingSubmission?.passkey
    },
    formUrl() {
      if (!this.formResponsePasskey) return null
      return `${config.get('forms2.app.url')}forms/${this.formResponsePasskey}`
    },
    shouldEmitMessageToParent() {
      return this.isEmbedded && window.parent && window.parent !== window
    },
    stepStatus() {
      return this.$route?.query?.status ?? null
    },
  },

  beforeDestroy() {
    this.removeMessageListener()
  },

  async mounted() {
    this.loading = true
    await this.getExistingInterviewSubmission()
    this.setupMessageListener()
    this.selectedTemplateId = this.templateOptions[0].templateId
    this.loading = false
  },

  methods: {
    ...mapActions('candidate', [
      'getUserDetails',
      'completeRegistrationInterview',
      'getRegistrationStepSubmission',
    ]),
    ...mapActions('forms', [
      'getFormLatestVersion',
      'createFormSubmission',
      'deleteFormSubmission',
    ]),
    reset() {
      this.loading = false
      this.isReadOnly = true
      this.newSubmission = null
      this.existingSubmission = null
    },
    async goBack() {
      this.loading = true
      const { errors } = await this.deleteFormSubmission(
        this.existingSubmission?.passkey ?? this.newSubmission?.passkey
      )

      if (errors.length > 0) {
        this.errorMessage = this.$t(
          'forms.interview.errors.failDeleteFormSubmission',
          this.newSubmission?.id || ''
        )
        this.loading = false
        return
      }

      this.reset()
      this.loading = false
    },

    async getExistingInterviewSubmission() {
      const { data } = await this.getRegistrationStepSubmission({
        candidateId: this.candidateId,
        registrationId: this.registrationId,
        stepCodeName: RegistrationStepCodeName.Interview,
      })

      if (data?.passkey) {
        this.existingSubmission = data
        this.isReadOnly = data.isSubmitted
        return
      }

      if (this.stepStatus === RegistrationStepStatus.Complete) {
        this.completeWithNoSubmission = true
      }
    },

    async handleCreateFormSubmission() {
      this.loading = true

      const { data: formTemplate, errors: getVersionErrors } =
        await this.getFormLatestVersion(this.selectedTemplateId)

      if (getVersionErrors.length > 0) {
        this.errorMessage = this.$t(
          'forms.interview.errors.failGetLatestFormVersion'
        )
        this.reset()
        return
      }

      const { id: formId, versionNo: formVersionNo } = formTemplate

      const { data, errors } = await this.createFormSubmission({
        formId,
        formVersionNo,
        values: {
          _candidate_id: this.candidateId,
          _registration_id: this.registrationId,
          _registration_step: RegistrationStepCodeName.Interview,
        },
      })

      if (errors.length > 0) {
        this.errorMessage = this.$t(
          'forms.interview.errors.failCreateFormSubmission'
        )
        this.reset()
        return
      }

      this.newSubmission = data
      this.isReadOnly = false

      this.loading = false
    },

    async handleCompleteInterview() {
      this.loading = true

      const { errors } = await this.completeRegistrationInterview({
        candidateId: this.candidateId,
        registrationId: this.registrationId,
      })

      if (errors.length > 0) {
        this.errorMessage = this.$t(
          'forms.interview.errors.failCompleteInterview'
        )

        this.reset()
        return
      }

      if (this.shouldEmitMessageToParent) {
        window.parent.postMessage(
          { completed: !errors.length },
          this.targetOrigin
        )
      }

      this.existingSubmission = this.newSubmission

      this.isReadOnly = true
      this.loading = false
    },

    // #region Iframe handlers
    setupMessageListener() {
      if (this.hasMessageListener) return
      addEventListener('message', this.handleMessage)
      this.hasMessageListener = true
    },

    removeMessageListener() {
      if (this.hasMessageListener) {
        removeEventListener('message', this.handleMessage)
        this.hasMessageListener = false
      }
    },

    async handleMessage(event) {
      const allowedOrigins = config.get('forms2.allowedOrigins').split(',')

      if (
        !allowedOrigins.some(
          (origin) => event.origin === origin || origin === '*'
        )
      )
        return

      if (event.data?.submission_type && event.data?.submitted === 'false') {
        this.errorMessage = this.$t(
          'forms.interview.errors.failforms2Submit',
          this.newSubmission?.id || ''
        )
        this.reset()
      }

      if (event.data?.submission_type && event.data?.submitted === 'true') {
        await this.handleCompleteInterview()
      }
    },

    // #endregion
  },
}
</script>

<template>
  <v-card class="pa-6 interview-form-container overflow-y-auto p-relative">
    <transition name="slide-down">
      <v-alert
        v-if="!!errorMessage"
        type="error"
        tile
        dense
        class="text-body-1 mb-0 w-100 p-absolute top-0 left-0"
      >
        {{ errorMessage || '' }}
      </v-alert>
    </transition>
    <div class="d-flex flex-column h-100 align-center">
      <div class="d-flex align-center w-100 justify-center">
        <BaseButton
          v-if="!isReadOnly"
          text
          prepend-icon="mdi-arrow-left"
          class="ma-8 cursor-pointer"
          @click="goBack"
        >
          {{ $t('general.back') }}
        </BaseButton>

        <p v-if="isReadOnly && !isEmbedded" class="text-h4 text-center my-12">
          {{ title }}
        </p>

        <v-spacer v-else />
      </div>
      <transition name="fade-down" mode="out-in">
        <v-select
          v-if="showCreateSubmissionUI"
          v-model="selectedTemplateId"
          :items="templateOptions"
          outlined
          hide-details
          item-text="title"
          item-value="templateId"
          :label="$t('forms.interview.formTypeSelectLabel')"
          class="w-100 mx-auto flex-shrink-0 registration-select mb-12"
        />
      </transition>

      <transition name="fade-down" mode="out-in">
        <Spinner
          v-if="loading"
          size="60"
          class="flex-grow-1 align-center spinner"
        />
        <v-card
          v-else-if="completeWithNoSubmission"
          outlined
          elevation="0"
          width="500"
          class="my-12 rounded-lg d-flex flex-column align-center pa-8"
        >
          <v-icon size="90" color="error lighten-2"
            >mdi-file-document-remove-outline</v-icon
          >
          <p class="text-body-1 text-center my-8 px-4 lh-200">
            {{ $t('forms.interview.completeWithNoSubmission') }}
          </p>
          <p class="text-subtitle-2 text-center px-4 lh-200">
            {{ $t('forms.interview.legacyRegistrationDisclaimer') }}
          </p>
        </v-card>
        <FormSubmissionContainer
          v-else-if="showSubmissionContainer"
          :key="formResponsePasskey || selectedTemplateId"
          :passkey="formResponsePasskey"
          :form-template-id="!formResponsePasskey ? selectedTemplateId : null"
          class="w-100 flex-grow-1 overflow-y-auto overflow-x-hidden pt-1"
        />

        <div
          v-else
          class="pt-1 w-100 fill-height flex-grow-1 overflow-y-hidden"
        >
          <iframe
            :src="formUrl"
            class="fill-height w-100"
            frameborder="0"
          ></iframe>
        </div>
      </transition>

      <transition name="fade-down" mode="out-in">
        <BaseButton
          v-if="showCreateSubmissionUI"
          size="xl"
          class="my-12 mx-auto"
          width="300"
          @click="handleCreateFormSubmission"
        >
          {{ $t('forms.interview.cta', [selectedTemplate.title]) }}
        </BaseButton>
      </transition>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
@import '@/scss/_vue-transitions.scss';

.interview-form-container {
  height: 100%;
  min-height: 800px;
  max-height: 90vh;
}

.registration-select {
  max-width: 600px;
}

.spinner {
  height: 100%;
  max-height: 500px;
}
</style>
